import useOnlineProductQuery from "@nitro/gatsby-source-harvia-pim/hooks/useOnlineProductQuery"
import { ExternalMedia, ProductValue } from "@nitro/gatsby-source-harvia-pim/types/plugin"
import React, { useContext, useEffect, useRef, useState } from "react"
import { PageContext } from "../../../context/PageContext"
import { useUserPreferences } from "../../../context/UserContext"
import useDatoMicrocopy from "../../../hooks/useDatoMicrocopy"
import useSiteMetadata from "../../../hooks/useSiteMetadata"
import { isBrowser } from "../../../utils"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import Pagination from "../../molecules/Pagination"
import ProductList from "../../molecules/ProductList"
import * as styles from "./ProductFilter.module.scss"
import { productCategoriesForArea } from "./utils"

/**
 * Tuotedata siinä muodossa kuin ProductFilter sen tarvitsee
 */
export type ProductFilterProduct = {
  identifier: string
  categories: string[]
  created?: string
  media?: ExternalMedia[]
  /**
      "item_code_harvia_finland",
      "external_media_textarea",
      "product_marketing_name_short",
      "product_family",
      "product_life_cycle_status",
      "control_type",
      "available_from_date",
      "product_group_2",
      "control_panel_type",
   */
  values?: ProductValue[]
  /** Spare parts association quantity */
  quantityNeeded?: string
  enabled?: boolean
  name?: string
}

type ProductListHeadingProps = {
  title?: string
  category?: string
  description?: string
}
/** Heading texts */
const ProductListHeading = (props: ProductListHeadingProps) => {
  return (
    <div className={styles.heading}>
      {props.title && (
        <>
          {props.category && <p>{props.category}</p>}
          <h2>{props.title}</h2>
        </>
      )}
      {props.description && <p>{props.description}</p>}
    </div>
  )
}

type OnlineProductListProps = {
  query?: string
  pageSize?: number
  sort?: string
  hideIfEmpty?: boolean
  /** Buildin aikana saadaan tuotteen assisiaatioiden vaaditut määrät, jotka syötetään tarvittaessa mukaan {<sku>:<count>} -objektina  */
  quantities?: Record<string, string>
} & ProductListHeadingProps

/**  Online list handler */
const OnlineProductList = (props: OnlineProductListProps) => {
  const { locale, localePim } = useContext(PageContext)
  const userPrefs = useUserPreferences()
  const t = useDatoMicrocopy("common", locale)

  const [products, setProducts] = useState<ProductFilterProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const { pim } = useSiteMetadata()

  const { pageSize = 16 } = props

  /* Tarkistetaan onko queryssä product_life_cycle_status toiveita, tai muutoin lisätään oletusarvot */
  let query = props.query || ""
  if (!query.includes("product_life_cycle_status")) {
    query = [props.query, "product_life_cycle_status:s3_maintenance", "product_life_cycle_status:s4_rampdown"].join()
  }

  // Lisätään and -operaattori kaikkiin verkkosivuston siteMetadatassa määriteltyihin hakuehtoihin, jolloin niistä tulee rajaavia ehtoja
  /* const baseParams = pim.baseParams.filter(Boolean).map(param => {
    const [key, ...rest] = param.split(":")
    return [key, "and", ...rest].join(":")
  }) */

  /* if (baseParams.length) {
    query = [baseParams, query].join(",")
  } */

  // Lisätään maakohtaiset filtterit tuotehakuun
  const countryProductFilters = userPrefs.localeArea
    ? productCategoriesForArea(userPrefs.localeArea).map(cat => `categories:${cat}`)
    : []
  const baseQuery = [...(pim?.baseParams || []), ...countryProductFilters].join(",")

  // Haetaan tuotteet
  const [onlineProducts, queryResult] = useOnlineProductQuery(
    query,
    {
      language: localePim,
      size: pageSize.toString(),
      page: page.toString(),
      sort: props.sort || "item_code_harvia_finland,asc",
      includeDisabledProducts: true,
    },
    {
      baseQuery,
    },
    // Projection arvot, jotka tuotteelta haetaan. Nämä pitää olla samat kuin mitä ProductList käyttää.
    [
      "item_code_harvia_finland",
      "external_media_textarea",
      "product_marketing_name_short",
      "product_family",
      "product_life_cycle_status",
      "control_type",
      "available_from_date",
      "product_group_2",
      "control_panel_type",
    ]
  )
  useEffect(() => {
    setPage(0)
    setProducts([])
    setLoading(true)
  }, [query, baseQuery, props.sort])

  useEffect(() => {
    // console.debug("ProductFilter useEffect()", onlineProducts, products)
    if (onlineProducts) {
      if (
        !products.length ||
        (products.length &&
          onlineProducts.length &&
          products[products.length - 1].identifier != onlineProducts[onlineProducts.length - 1].identifier) // tarkistetaan onko uutta dataa
      ) {
        setProducts([...products, ...onlineProducts])
      } else {
        // why clone and set to the same but new value? To trigger re-render?
        setProducts([...products])
      }

      setLoading(false)
    }
  }, [onlineProducts])

  function nextPage() {
    setPage(page + 1)
    setLoading(true)
  }

  if (!loading && props.hideIfEmpty && !products?.length) return null

  // Lisätään määrät, jos saatavilla. Näistä tulee tuotelistauksessa labeleita.
  if (props.quantities) {
    products.forEach(product => (product.quantityNeeded = props.quantities?.[product.identifier]))
  }

  return (
    <>
      <ProductListHeading {...props} />
      {products?.length ? <ProductList products={products} /> : <div>- {t("noResults", "No results")} -</div>}
      <LoadingSpinner loading={loading} />
      {!loading && (
        <Pagination
          page={queryResult?.number || 0}
          pageSize={pageSize}
          totalElements={queryResult?.totalElements || 0}
          onNextPage={nextPage}
        />
      )}

      <hr />
    </>
  )
}

export type OfflineProductListProps = {
  /** Staattista tuotedataa ssr renderointiin */
  products?: ProductFilterProduct[]
  pageSize?: number
  hideIfEmpty?: boolean
} & ProductListHeadingProps

/** Offline list handler */
export const OfflineProductList: React.FC<OfflineProductListProps> = props => {
  const [page, setPage] = React.useState(0)
  const pageSize = props.pageSize || 48

  const products = props.products?.slice(page, (page + 1) * pageSize)

  if (!products?.length && props.hideIfEmpty) return null

  return (
    <>
      <ProductListHeading {...props} />
      {products && <ProductList products={products} />}
      <Pagination
        page={page}
        pageSize={pageSize}
        totalElements={props.products?.length || 0}
        onNextPage={() => setPage(page + 1)}
      />

      <hr />
    </>
  )
}

export type ProductFilterProps = { lazyLoading?: boolean } & OnlineProductListProps & OfflineProductListProps

/** Yhdistetty Online ja Offline tuotelistaukset */
const ProductFilter: React.FC<ProductFilterProps> = props => {
  //console.debug("Rendering ProductFilter", props)

  const container = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState<boolean>(!props.lazyLoading)

  const onlineMode = !!props.query

  useEffect(() => {
    onlineMode && props.lazyLoading && window.addEventListener("scroll", scrolling)

    function scrolling(e: Event) {
      if (!shown && container.current && window.innerHeight > container.current?.getBoundingClientRect().top) {
        setShown(true)
        window.removeEventListener("scroll", scrolling)
      }
    }

    return () => {
      window.removeEventListener("scroll", scrolling)
    }
  }, [])

  return (
    <div className={styles.container} ref={container}>
      {isBrowser && onlineMode ? (
        shown && <OnlineProductList {...(props as OnlineProductListProps)} />
      ) : (
        <OfflineProductList {...(props as OfflineProductListProps)} />
      )}
    </div>
  )
}

export default ProductFilter
